import React, { FunctionComponent, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'

import { skipToken } from '@reduxjs/toolkit/query'
import { differenceInYears, parseISO } from 'date-fns'
import styled, { useTheme } from 'styled-components/native'

import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { BodyText, Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { InputField } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/InputField'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { SearchIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SearchIcon'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { TertiaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tertiaryButton/TertiaryButton'
import { Title, Size as TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { FullScreenOverlay } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/fullScreenOverlay/FullScreenOverlay'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { NpTestPlanLibraryTable } from './components/NpTestPlanLibraryTable'
import {
  filterCatalog,
  NP_TEST_PLAN_INFORMANT_ORDER,
  NP_TEST_PLAN_OLDEST_AGE_FILTER,
  toAgeRange,
} from './data/npTestPlanMeasureUtils'
import { useGetNpTestPlanMeasureCatalogQuery, useGetNpTestPlanSummaryQuery } from './data/npTestPlansApi'
import { NpTestPlanInformant, NpTestPlanMeasure, NpTestPlanMeasureType, NpTestPlanStatus } from './data/npTestPlanTypes'
import { useNpTestPlanMeasureLabels } from './hooks/useNpTestPlanMeasureLabels'
import { useUpdateNpTestPlanMeasures } from './hooks/useUpdateNpTestPlanMeasures'
import {
  FilterDropdown,
  FilterOption,
  getFilterDropdownItems,
  RadioFilterMenu,
} from '../../common/components/filterDropdown/FilterDropdown'
import { CLIENT_EVALUATION } from '../../common/constants/routingConstants'
import { getSelectedClientId } from '../../data/lyraTherapy/clientSelectors'
import { useSelectedClientData } from '../clients/clientDetails/data/hooks/useSelectedClientData'
import { useSelectedEpisodeForClient } from '../episodes/data/hooks/useSelectedEpisodeForClient'

const Page = styled.View<{ theme: ThemeType }>(({ theme: { colors } }) => ({
  flex: 1,
  backgroundColor: colors.backgroundPrimary,
}))

const Header = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: spacing['24px'],
  paddingHorizontal: spacing['24px'],
  paddingVertical: spacing['24px'],
  // Keeps the filter menus above the table.
  zIndex: 1,
}))

const HeaderContent = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flex: 1,
  gap: spacing['16px'],
}))

const TitleRow = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'baseline',
  gap: spacing['16px'],
}))

const FilterRow = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['16px'],
}))

const SEARCH_FIELD_STYLE = { marginBottom: 0 }

const SearchContainer = styled.View({
  width: '440px',
})

const Chips = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['8px'],
}))

const HeaderActions = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['16px'],
}))

const Focus: FunctionComponent = ({ children }) => (
  <FullScreenOverlay isOpen isFocused={false} renderInPortal>
    <Page testID={tID('NpTestPlanMeasureLibrary')}>{children}</Page>
  </FullScreenOverlay>
)

const ALL = 'all'

const AGE_FILTERS = Array.from({ length: NP_TEST_PLAN_OLDEST_AGE_FILTER }, (_, index) => index + 1)

const AGE_MENU_MAX_HEIGHT = 360

const enumValue = <T extends string>(values: Record<string, T>, value: string | null): T | null =>
  Object.values(values).find((option) => option === value) ?? null

export const NpTestPlanMeasureLibrary: FunctionComponent = () => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { colors } = useTheme() as ThemeType
  const { formatInformant } = useNpTestPlanMeasureLabels()

  const patientId = useSelector(getSelectedClientId)
  const { data: clientDetails } = useSelectedClientData()
  const { data: episode, isLoading: isLoadingEpisode } = useSelectedEpisodeForClient() as {
    data: Episode | undefined
    isLoading: boolean
  }

  const { data: summary, isLoading: isLoadingSummary } = useGetNpTestPlanSummaryQuery(
    episode?.id && patientId ? { episodeId: episode.id, patientId } : skipToken,
  )
  const { updateMeasures, isSaving } = useUpdateNpTestPlanMeasures(episode?.id)

  const isDraft = summary?.status === NpTestPlanStatus.DRAFT
  const { data: catalog = [], isLoading: isLoadingCatalog } = useGetNpTestPlanMeasureCatalogQuery(
    isDraft && summary?.modality ? summary.modality : skipToken,
  )

  const measureType = enumValue(NpTestPlanMeasureType, searchParams.get('measureType'))
  const informant = enumValue(NpTestPlanInformant, searchParams.get('informant'))

  const [search, setSearch] = useState('')
  const [informantFilter, setInformantFilter] = useState<NpTestPlanInformant | null>(informant)
  const [pickedAge, setPickedAge] = useState<number | null>()
  const [addedIds, setAddedIds] = useState<string[]>([])

  const patientAge = clientDetails?.date_of_birth
    ? differenceInYears(new Date(), parseISO(clientDetails.date_of_birth))
    : null

  const ageFilter = pickedAge === undefined ? patientAge : pickedAge

  const planIds = useMemo(() => new Set(summary?.measures.map(({ id }) => id)), [summary?.measures])
  const selectedIds = useMemo(() => new Set([...planIds, ...addedIds]), [planIds, addedIds])

  const offered = useMemo(
    () =>
      measureType
        ? filterCatalog(catalog, {
            measureType,
            informant: informantFilter,
            ageRange: toAgeRange(patientAge, pickedAge),
            search,
            planIds,
          })
        : ([] as NpTestPlanMeasure[]),
    [catalog, measureType, informantFilter, patientAge, pickedAge, search, planIds],
  )

  const memberName = clientDetails?.preferred_first_name || clientDetails?.first_name || ''

  if (isLoadingEpisode || isLoadingSummary) {
    return (
      <Focus>
        <LoadingIndicator topPadding={0} />
      </Focus>
    )
  }

  if (!isDraft || !measureType || !informant) {
    return <Navigate to={CLIENT_EVALUATION.route} replace />
  }

  const returnToPlan = () => navigate(CLIENT_EVALUATION.route)

  const onDone = async () => {
    if (!addedIds.length || (await updateMeasures([...planIds, ...addedIds]))) {
      returnToPlan()
    }
  }

  const onToggle = ({ id }: NpTestPlanMeasure) =>
    setAddedIds((current) => (current.includes(id) ? current.filter((added) => added !== id) : [...current, id]))

  const informantName =
    informant === NpTestPlanInformant.SELF_REPORT && memberName ? memberName : formatInformant(informant)

  const title =
    measureType === NpTestPlanMeasureType.RATING_SCALE
      ? formatMessage(
          {
            defaultMessage: 'Add scales for {informantName}',
            description: 'Title of the page for adding rating scales to a neuropsych test plan',
          },
          { informantName },
        )
      : formatMessage(
          {
            defaultMessage: 'Add tests for {informantName}',
            description: 'Title of the page for adding face-to-face tests to a neuropsych test plan',
          },
          { informantName },
        )

  const searchPlaceholder =
    measureType === NpTestPlanMeasureType.RATING_SCALE
      ? formatMessage({
          defaultMessage: 'Search by rating scale name',
          description: 'Placeholder for the box that searches the library of rating scales',
        })
      : formatMessage({
          defaultMessage: 'Search by test name',
          description: 'Placeholder for the box that searches the library of face-to-face tests',
        })

  const allLabel = formatMessage({
    defaultMessage: 'All',
    description: 'Selected value of a measure library filter that shows every option',
  })

  const informantOptions: FilterOption<string>[] = [
    { value: ALL, label: allLabel },
    ...NP_TEST_PLAN_INFORMANT_ORDER.map((option) => ({ value: option, label: formatInformant(option) })),
  ]

  const ageOptions: FilterOption<string>[] = [
    { value: ALL, label: allLabel },
    ...AGE_FILTERS.map((age) => ({
      value: String(age),
      label: age === NP_TEST_PLAN_OLDEST_AGE_FILTER ? `${age}+` : String(age),
    })),
  ]

  const selectedInformant = informantFilter ?? ALL
  const selectedAge = ageFilter === null ? ALL : String(ageFilter)
  // Unpicked, the chip shows the member's exact age, which the open-ended oldest option doesn't stand for.
  const selectedAgeLabel =
    pickedAge === undefined && patientAge !== null
      ? selectedAge
      : (ageOptions.find(({ value }) => value === selectedAge)?.label ?? allLabel)
  const selectedAgeOption =
    ageOptions.find(({ value, label }) => value === selectedAge && label === selectedAgeLabel)?.value ?? ''

  return (
    <Focus>
      <Header>
        <HeaderContent>
          <TitleRow>
            <Title size={TitleSize.SMALL} text={title} />
            {Boolean(memberName) && (
              <BodyText
                size={BodyTextSize.SMALL}
                color={colors.textSecondary}
                text={formatMessage(
                  {
                    defaultMessage: 'Client: {memberName}',
                    description: 'Names the member whose neuropsych test plan is being added to',
                  },
                  { memberName },
                )}
              />
            )}
          </TitleRow>
          <FilterRow>
            <SearchContainer>
              <InputField
                testID={tID('NpTestPlanMeasureLibrary-search')}
                name='NpTestPlanMeasureLibrary-search'
                value={search}
                // `InputField` drops `onChangeText`.
                onChange={(event) => setSearch(event.nativeEvent.text)}
                placeholder={searchPlaceholder}
                leftIcon={<SearchIcon />}
                baseInputStyle={SEARCH_FIELD_STYLE}
              />
            </SearchContainer>
            <Chips>
              <FilterDropdown
                customDropdownMenuComponent={RadioFilterMenu}
                customDropdownMenuComponentProps={{ options: informantOptions, selectedValue: selectedInformant }}
                dropdownItems={getFilterDropdownItems(informantOptions, (value) =>
                  setInformantFilter(enumValue(NpTestPlanInformant, value)),
                )}
                isHighlighted
                label={formatMessage({
                  defaultMessage: 'Informant',
                  description: 'Label for the filter that narrows the measure library by informant',
                })}
                selectedLabel={informantOptions.find(({ value }) => value === selectedInformant)?.label ?? allLabel}
                shouldCloseMenuOnOptionPress
                testId='NpTestPlanMeasureLibrary-informant'
                zIndex={2}
              />
              <FilterDropdown
                customDropdownMenuComponent={RadioFilterMenu}
                customDropdownMenuComponentProps={{ options: ageOptions, selectedValue: selectedAgeOption }}
                dropdownItems={getFilterDropdownItems(ageOptions, (value) =>
                  setPickedAge(value === ALL ? null : Number(value)),
                )}
                isHighlighted
                label={formatMessage({
                  defaultMessage: 'Age',
                  description: 'Label for the filter that narrows the measure library by age',
                })}
                menuMaxHeight={AGE_MENU_MAX_HEIGHT}
                selectedLabel={selectedAgeLabel}
                shouldCloseMenuOnOptionPress
                testId='NpTestPlanMeasureLibrary-age'
                zIndex={1}
              />
            </Chips>
          </FilterRow>
        </HeaderContent>
        <HeaderActions>
          <TertiaryButton
            testID={tID('NpTestPlanMeasureLibrary-exit')}
            onPress={returnToPlan}
            customTextColor={colors.linkDefault}
            text={formatMessage({
              defaultMessage: 'Exit',
              description: 'Leaves the measure library without adding anything to the test plan',
            })}
          />
          <PrimaryButton
            testID={tID('NpTestPlanMeasureLibrary-done')}
            onPress={onDone}
            loading={isSaving}
            text={formatMessage({
              defaultMessage: 'Done',
              description: 'Adds the ticked measures to the test plan and returns to it',
            })}
          />
        </HeaderActions>
      </Header>
      {isLoadingCatalog ? (
        <LoadingIndicator topPadding={0} />
      ) : (
        <NpTestPlanLibraryTable measures={offered} selectedIds={selectedIds} planIds={planIds} onToggle={onToggle} />
      )}
    </Focus>
  )
}

export default NpTestPlanMeasureLibrary
